import {
  createContext, useContext, useEffect, useMemo
} from 'react';

import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { ZBError } from '@zenbusiness/application-commons-logging';

import { ENABLE_BLR_SPECIFIC_CONTENT } from '#client/constants/experimentsFlags';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';
import { VELO_BANNER_DEFAULT } from '#client/pages/ProductLanding/components/HeroSection/constants';
import { PRODUCTS_KEY } from '#client/pages/Products/constants';

import { useProductDetails } from './useProductDetails';
import { useFormationStatus } from '../hooks/useFormationStatus';
import { useTaxClassification } from '../hooks/useTaxClassification';

const ProductLandingContext = createContext();

const useProductLandingContext = () => {
  const context = useContext(ProductLandingContext);

  if (!context) {
    throw new ZBError('useProductLanding must be used within a ProductLandingContext.Provider');
  }

  return context;
};

// Helper to resolve config value - can be a function or a static value
const resolveConfig = (config, context) => (
  typeof config === 'function' ? config(context) : config
);

const ProductLandingProvider = ({ children, federated, productId }) => {
  const navigate = useNavigate();
  const productsPath = useDashboardPath(`/${PRODUCTS_KEY}`);
  const productConfig = useProductDetails(productId);
  const formationStatus = useFormationStatus();
  const { entitlements } = useBusinessEntity();
  const { orderTasksWithStatus } = useOrderTrackerStatus();

  // Check if product has a feature flag gate configuration
  const { flagKey, redirectPath } = productConfig?.productGates?.flagGate || {};
  // Only evaluate flags if product has a flagGate config, otherwise skip evaluation.
  const isFlagEnabled = useExperimentFlag(
    flagKey || '',
    false,
    false,
    !flagKey
  );

  const fallbackRedirectPath = useDashboardPath(redirectPath || `/${PRODUCTS_KEY}`);

  // Check business-licenses flag for redirect to business-license-report
  const isBusinessLicenseReportEnabled = useExperimentFlag(
    productId === 'business-licenses' ? ENABLE_BLR_SPECIFIC_CONTENT : '',
    false,
    false,
    productId !== 'business-licenses'
  );
  const businessLicenseReportPath = useDashboardPath(`/${PRODUCTS_KEY}/business-license-report`);

  // Fetch tax classification when product requires it
  const { taxClassification } = useTaxClassification({
    skip: !productConfig?.isTaxClassificationDependent
  });

  useEffect(function redirects() {
    // Redirect business-licenses to business-license-report when flag is enabled
    if (productId === 'business-licenses' && isBusinessLicenseReportEnabled) {
      navigate(businessLicenseReportPath, { replace: true });
      return;
    }

    // Redirect if product has a flagGate config and the flag is disabled
    if (flagKey && !isFlagEnabled) {
      navigate(fallbackRedirectPath, { replace: true });
      return;
    }

    // Redirect to products list page when product is not found
    if (!productConfig) {
      navigate(productsPath, { replace: true });
    }
  }, [
    navigate,
    productsPath,
    productConfig,
    flagKey,
    isFlagEnabled,
    fallbackRedirectPath,
    productId,
    isBusinessLicenseReportEnabled,
    businessLicenseReportPath
  ]);

  // Extract hero configuration with defaults
  const heroConfig = useMemo(() => {
    const hero = productConfig?.hero;
    if (!hero) return {};

    return {
      packages: hero.buyBox || [],
      veloBanner: hero.veloBanner || VELO_BANNER_DEFAULT,
      comparePackages: hero.comparePackages,
      plans: hero.plans,
      getBuyButtonConfig: productConfig.getBuyButtonConfig || (() => ({
        isBuyEnabled: false,
        ctaText: 'productPage.common.buyBox.buttonDisabled.label',
        ctaDisabledTooltip: 'productPage.common.buyBox.buttonDisabled.tooltip'
      }))
    };
  }, [productConfig]);

  const isFederated = Boolean(federated);

  // Build base context first (needed for resolving function-based configs)
  const baseContext = useMemo(() => ({
    isFederated,
    productConfig,
    productId,
    formationStatus,
    entitlements,
    orderTasksWithStatus,
    taxClassification,
    ...heroConfig
  }), [isFederated, productConfig, productId, formationStatus, entitlements, heroConfig,
    orderTasksWithStatus, taxClassification]);

  // Resolve all function-based configs and merge into productConfig
  const resolvedProductConfig = useMemo(() => {
    if (!productConfig) return productConfig;

    const {
      hero, benefit, banner, footnotes, howItWorks, whatsIncluded
    } = productConfig;

    return {
      ...productConfig,
      banner: resolveConfig(banner, baseContext),
      benefit: resolveConfig(benefit, baseContext),
      footnotes: resolveConfig(footnotes, baseContext),
      hero: hero && {
        ...hero,
        subheader: resolveConfig(hero.subheader, baseContext)
      },
      howItWorks: howItWorks && {
        ...howItWorks,
        items: resolveConfig(howItWorks.items, baseContext)
      },
      whatsIncluded: whatsIncluded && {
        ...whatsIncluded,
        items: resolveConfig(whatsIncluded.items, baseContext)
      }
    };
  }, [productConfig, baseContext]);

  const contextValue = useMemo(() => ({
    ...baseContext,
    productConfig: resolvedProductConfig
  }), [baseContext, resolvedProductConfig]);

  // Render content based on product component gate configuration
  const content = (() => {
    const gateComponents = productConfig?.productGates?.componentGates || [];

    if (!Array.isArray(gateComponents) || !gateComponents.length) {
      return children;
    }

    // Nest component gates from last to first, wrapping each one around the previous one, with children at the innermost position (most nested component)
    return gateComponents.reduceRight(
      (acc, { Component, props }) => (
        <Component {...props}>
          {acc}
        </Component>
      ),
      children
    );
  })();

  return (
    <ProductLandingContext.Provider value={contextValue}>
      {content}
    </ProductLandingContext.Provider>
  );
};

ProductLandingProvider.propTypes = {
  children: PropTypes.node.isRequired,
  federated: PropTypes.bool,
  productId: PropTypes.string.isRequired
};

export { ProductLandingProvider, useProductLandingContext };
